import React, { useState, useEffect } from 'react'
import AdminLayout from '@/layouts/AdminLayout'
import apiClient from '@services/api/client'
import { usePermissions } from '@/hooks/usePermissions'
import { Navigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import ConfirmModal from '@/components/ui/ConfirmModal'

const AdminSystemControlPage = () => {
  const { canAccessSystemControl } = usePermissions()
  const [logs, setLogs] = useState([])
  const [stats, setStats] = useState({
    totalLogs: 0,
    errors: 0,
    warnings: 0,
    info: 0,
    critical: 0
  })
  const [loading, setLoading] = useState(true)
  const [filterLevel, setFilterLevel] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState('today')
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  // Vérifier l'accès
  if (!canAccessSystemControl) {
    return <Navigate to="/admin/dashboard" replace />
  }

  useEffect(() => {
    loadLogs()
    loadStats()
  }, [filterLevel, filterType, dateRange])

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadLogs()
        loadStats()
      }, 10000) // Refresh toutes les 10 secondes
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const loadLogs = async () => {
    try {
      const data = await apiClient.get('/logs', { params: { level: filterLevel, type: filterType, date_range: dateRange } })
      if (data.success) {
        setLogs(data.data || data.logs || [])
      }
    } catch (error) {
      console.error('Erreur chargement logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const data = await apiClient.get('/logs/stats')
      if (data.success) {
        setStats(data.data || data.stats || stats)
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error)
    }
  }

  const handleClearLogsClick = () => {
    setShowConfirmModal(true)
  }

  const clearLogs = async () => {
    try {
      await apiClient.delete('/logs')
      toast.success('Logs supprimés avec succès')
      loadLogs()
      loadStats()
    } catch (error) {
      toast.error('Erreur lors de la suppression des logs')
    }
  }

  const getLevelColor = (level) => {
    const colors = {
      'info': 'bg-blue-100 text-blue-700',
      'warning': 'bg-yellow-100 text-yellow-700',
      'error': 'bg-red-100 text-red-700',
      'critical': 'bg-purple-100 text-purple-700'
    }
    return colors[level] || 'bg-gray-100 text-gray-700'
  }

  const getLevelIcon = (level) => {
    const icons = {
      'info': '📘',
      'warning': '⚠️',
      'error': '❌',
      'critical': '🚨'
    }
    return icons[level] || 'ℹ️'
  }

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (log.type && log.type.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesSearch
  })

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5B700] mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du contrôle système...</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-md border border-slate-200 p-8 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Contrôle Système</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Surveillance et maintenance du système
              </p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 text-[#B8860B] rounded"
                />
                <span className="text-gray-700">Auto (10s)</span>
              </label>
              <button
                onClick={() => { loadLogs(); loadStats(); }}
                className="px-6 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all duration-200 flex items-center gap-2 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Actualiser
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-md p-6 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Logs</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalLogs}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center text-2xl">
                📊
              </div>
            </div>
          </div>

          <div className="bg-white rounded-md p-6 border border-red-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Erreurs</p>
                <p className="text-2xl font-bold text-red-600">{stats.errors}</p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-md flex items-center justify-center text-2xl">
                ❌
              </div>
            </div>
          </div>

          <div className="bg-white rounded-md p-6 border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Critiques</p>
                <p className="text-2xl font-bold text-purple-600">{stats.critical}</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-md flex items-center justify-center text-2xl">
                🚨
              </div>
            </div>
          </div>

          <div className="bg-white rounded-md p-6 border border-yellow-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Avertissements</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.warnings}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-md flex items-center justify-center text-2xl">
                ⚠️
              </div>
            </div>
          </div>

          <div className="bg-white rounded-md p-6 border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Info</p>
                <p className="text-2xl font-bold text-blue-600">{stats.info}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-md flex items-center justify-center text-2xl">
                📘
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-md p-6 mb-6 border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
              >
                <option value="all">Tous les niveaux</option>
                <option value="info">Info</option>
                <option value="warning">Avertissement</option>
                <option value="error">Erreur</option>
                <option value="critical">Critique</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
              >
                <option value="all">Tous les types</option>
                <option value="auth">Authentification</option>
                <option value="database">Base de données</option>
                <option value="api">API</option>
                <option value="system">Système</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
              >
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
                <option value="all">Tout</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher dans les logs..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClearLogsClick}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Effacer tous les logs
            </button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-md overflow-hidden border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Niveau
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <div className="text-6xl mb-4">📭</div>
                      <p className="text-lg font-medium">Aucun log trouvé</p>
                      <p className="text-sm mt-2">Aucune entrée ne correspond à vos critères de recherche</p>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getLevelColor(log.level)}`}>
                          <span>{getLevelIcon(log.level)}</span>
                          {log.level}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 font-medium">
                          {log.type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-800 max-w-md truncate" title={log.message}>
                          {log.message}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {log.user_name || 'Système'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-500">
                          {new Date(log.timestamp || log.created_at).toLocaleString('fr-FR')}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de confirmation */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={clearLogs}
        type="danger"
        title="Supprimer tous les logs ?"
        message="Cette action est irréversible. Tous les logs système seront définitivement supprimés."
        confirmText="Oui, supprimer"
        cancelText="Annuler"
      />
    </AdminLayout>
  )
}

export default AdminSystemControlPage
